import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, LineChart as LcIcon, BarChart3, Users, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';
import LiquidityPdfExport from './LiquidityPdfExport';
import LiquidityBatchExport from './LiquidityBatchExport';

interface LiquidityDashboardProps {
  companyId?: string;
}

export default function LiquidityDashboard({ companyId }: LiquidityDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [priceTrend, setPriceTrend] = useState<any[]>([]);
  const [volTrend, setVolTrend] = useState<any[]>([]);
  const [top, setTop] = useState<any[]>([]);
  const [recent, setRecent] = useState<any[]>([]);
  const [days, setDays] = useState(30);
  const [note, setNote] = useState('');
  const [generatingNote, setGeneratingNote] = useState(false);

  async function load() {
    try {
      setLoading(true);
      const params = companyId ? { p_company: companyId } : {};
      const [{ data: p }, { data: v }, { data: t }, { data: r }] = await Promise.all([
        supabase.rpc('liq_price_trend', params),
        supabase.rpc('liq_volume_trend', params),
        supabase.rpc('liq_top_participants', { ...(params as any), period_days: days }),
        supabase.rpc('liq_recent_trades', { ...(params as any), limit_n: 50 }),
      ]);
      setPriceTrend(p || []);
      setVolTrend(v || []);
      setTop(t || []);
      setRecent(r || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function generateNote() {
    try {
      setGeneratingNote(true);
      setNote('Generating market note…');
      const { data, error } = await supabase.functions.invoke('ella-liquidity-note', {
        body: { companyId: companyId || null, periodDays: days },
      });
      if (error) throw error;
      setNote(data?.note || '');
    } catch (e: any) {
      setNote(`Error: ${e.message}`);
      toast.error('Failed to generate note');
    } finally {
      setGeneratingNote(false);
    }
  }

  useEffect(() => {
    load();
  }, [companyId, days]);

  const totalVol = useMemo(
    () => (volTrend || []).reduce((s: any, d: any) => s + (d.volume_shares || 0), 0),
    [volTrend]
  );

  if (loading)
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <div className="p-4 space-y-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-semibold">Liquidity Dashboard</h2>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={7}
            max={365}
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value || '30'))}
            className="w-24"
          />
          <LiquidityPdfExport companyId={companyId} periodDays={days} />
          <LiquidityBatchExport periodDays={days} />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid sm:grid-cols-3 gap-3">
        <Metric title="Total Volume (period)" value={totalVol.toLocaleString()} />
        <Metric title="Active Participants" value={new Set(top.map((t) => t.email)).size} />
        <Metric title="Recent Trades" value={recent.length} />
      </div>

      {/* Price trend */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LcIcon className="h-5 w-5" /> Average Price (Daily)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={priceTrend}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="avg_price"
                stroke="hsl(var(--primary))"
                name="Avg Price"
              />
              <Line
                type="monotone"
                dataKey="trades"
                stroke="hsl(var(--accent))"
                name="Trades/day"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Volume trend */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" /> Volume (Daily)
          </CardTitle>
        </CardHeader>
        <CardContent className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={volTrend}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="volume_shares" fill="hsl(var(--primary))" name="Shares/day" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top participants */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" /> Top Participants (last {days}d)
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Trades</TableHead>
                <TableHead className="text-right">Shares</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {top.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No participant data available
                  </TableCell>
                </TableRow>
              ) : (
                top.map((r: any) => (
                  <TableRow key={`${r.role}-${r.email}`}>
                    <TableCell>{r.role}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell>{r.trades}</TableCell>
                    <TableCell className="text-right">
                      {r.shares?.toLocaleString?.() ?? r.shares}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent trades */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle>Recent Trades</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Shares</TableHead>
                <TableHead>Price/Share</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Seller</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">
                    No trades found
                  </TableCell>
                </TableRow>
              ) : (
                recent.map((r: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{new Date(r.trade_time).toLocaleDateString()}</TableCell>
                    <TableCell>{r.company_name}</TableCell>
                    <TableCell>{r.share_class}</TableCell>
                    <TableCell>{r.shares?.toLocaleString?.() ?? r.shares}</TableCell>
                    <TableCell>{r.price_per_share}</TableCell>
                    <TableCell>{r.buyer_email}</TableCell>
                    <TableCell>{r.seller_email}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Ella market note */}
      <Card className="border-border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" /> Ella Market Note
          </CardTitle>
          <Button variant="outline" size="sm" onClick={generateNote} disabled={generatingNote}>
            <Sparkles className="h-4 w-4 mr-1" />
            {generatingNote ? 'Generating...' : 'Refresh Note'}
          </Button>
        </CardHeader>
        <CardContent>
          <pre className="whitespace-pre-wrap text-sm text-muted-foreground">
            {note || 'Click Refresh Note for an AI summary of recent liquidity.'}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}

function Metric({ title, value }: { title: string; value: string | number }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-1">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}
